import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BUTTON_TYPES } from '../constants/buttonTypes';
import { VALIDATION_PATTERNS } from '../constants/validationLimits';

/**
 * Shared validators for button fields
 */

/**
 * Validate phone number in E.164 format
 */
export const validatePhoneE164 = value => {
  if (!value) return true;
  return VALIDATION_PATTERNS.PHONE_E164.test(value);
};

/**
 * Validate URL format (http/https)
 */
export const validateUrl = value => {
  if (!value) return true;
  return VALIDATION_PATTERNS.URL_PROTOCOL.test(value);
};

/**
 * Validate that copy code is either a single variable or static text
 */
export const validateCopyCode = value => {
  if (!value) return true;

  const variableMatches = value.match(VALIDATION_PATTERNS.NUMERIC_VARIABLE);

  // No variables is fine
  if (!variableMatches) return true;

  // Single variable that is the entire value is fine
  if (variableMatches.length === 1 && value.trim() === variableMatches[0]) {
    return true;
  }

  // Multiple variables or mixed with text is not allowed
  return false;
};

/**
 * Validate that button text has no variables (except for VOICE_CALL)
 */
export const validateNoVariablesInText = (value, buttonType) => {
  if (!value) return true;
  // VOICE_CALL can have variables in title
  if (buttonType === BUTTON_TYPES.VOICE_CALL) return true;
  // Other types cannot have variables
  return !/\{\{\d+\}\}/.test(value);
};

/**
 * Validate that URL variables are only at the end
 */
export const validateUrlVariablesAtEnd = value => {
  if (!value) return true;

  const variableMatches = value.match(VALIDATION_PATTERNS.NUMERIC_VARIABLE);

  // No variables is fine
  if (!variableMatches) return true;

  // Check that variables only appear at the end
  const firstVariableIndex = value.indexOf('{{');
  const variablesPart = value.substring(firstVariableIndex);

  // Valid pattern: {{1}} or {{1}}/{{2}} etc. - only variables and slashes
  const validVariablesPattern = /^(\{\{\d+\}\})(\/\{\{\d+\}\})*$/;

  return validVariablesPattern.test(variablesPart);
};

/**
 * Validate TTL is within range
 */
export const validateTTL = (value, minTTL, maxTTL) => {
  if (!value) return false;
  const numValue = parseInt(value, 10);
  return numValue >= minTTL && numValue <= maxTTL;
};

/**
 * Composable for button validation logic (functional approach)
 * Works for both Twilio and WhatsApp templates
 *
 * @param {Ref<Array>} buttons - Ref to buttons array
 * @param {Object} config - Platform configuration
 * @param {string} config.i18nPrefix - i18n key prefix (e.g., 'CONTENT_TEMPLATES' or 'WHATSAPP_TEMPLATES')
 * @param {Object} config.limits - Platform limits object
 * @param {Object} config.fieldMapping - Field name mapping { title: 'title'|'text', phone: 'phone'|'phone_number', code: 'code'|'example' }
 * @param {boolean} [config.showUrlExample=false] - Whether URL buttons need example field
 * @param {boolean} [config.copyCodeShowsText=true] - Whether copy code buttons show text field
 */
export function useButtonValidation(buttons, config = {}) {
  const { t } = useI18n();

  const {
    i18nPrefix = 'CONTENT_TEMPLATES',
    limits = {},
    fieldMapping = {
      title: 'title',
      phone: 'phone',
      code: 'code',
    },
    showUrlExample = false,
    copyCodeShowsText = true,
  } = config;

  const titleField = fieldMapping.title || 'title';
  const phoneField = fieldMapping.phone || 'phone';
  const codeField = fieldMapping.code || 'code';

  const getTitleMaxLength = () =>
    limits.BUTTON_TEXT_MAX_LENGTH ??
    limits.CALL_TO_ACTION_BUTTON_TITLE_MAX_LENGTH ??
    25;

  const getCodeMaxLength = () =>
    limits.COPY_CODE_MAX_LENGTH ?? limits.CALL_TO_ACTION_CODE_MAX_LENGTH ?? 15;

  const getMinTTL = () =>
    limits.MIN_TTL_MINUTES ?? limits.CALL_TO_ACTION_MIN_TTL_MINUTES ?? 1;

  const getMaxTTL = () =>
    limits.MAX_TTL_MINUTES ?? limits.CALL_TO_ACTION_MAX_TTL_MINUTES ?? 43200;

  /**
   * Validate a single button and return errors
   */
  const validateButton = (button, index) => {
    const errors = {};
    const errorKeyPrefix = `${i18nPrefix}.CREATE.CALL_TO_ACTION`;

    // Validate title/text field (all types except COPY_CODE when copyCodeShowsText is false)
    const showTitleForType =
      button.type !== BUTTON_TYPES.COPY_CODE || copyCodeShowsText;
    if (showTitleForType) {
      const titleValue = button[titleField];
      const titleErrorKey = `button_${index}_${titleField}`;

      if (!titleValue?.trim()) {
        errors[titleErrorKey] = t(`${errorKeyPrefix}.ERROR_TEXT_REQUIRED`);
      } else if (titleValue.length > getTitleMaxLength()) {
        errors[titleErrorKey] = t(`${errorKeyPrefix}.TEXT_TOO_LONG`, {
          max: getTitleMaxLength(),
        });
      } else if (!validateNoVariablesInText(titleValue, button.type)) {
        errors[titleErrorKey] = t(
          `${errorKeyPrefix}.ERROR_NO_VARIABLES_IN_TEXT`
        );
      }
    }

    // Type-specific validations
    switch (button.type) {
      case BUTTON_TYPES.URL:
        if (!button.url?.trim()) {
          errors[`button_${index}_url`] = t(
            `${errorKeyPrefix}.ERROR_URL_REQUIRED`
          );
        } else if (!validateUrl(button.url)) {
          errors[`button_${index}_url`] = t(
            `${errorKeyPrefix}.URL_INVALID_PROTOCOL`
          );
        } else if (!validateUrlVariablesAtEnd(button.url)) {
          errors[`button_${index}_url`] = t(
            `${errorKeyPrefix}.ERROR_URL_VARIABLES_ONLY_AT_END`
          );
        }

        // Validate example if URL has variables (WhatsApp requirement)
        if (
          showUrlExample &&
          button.url?.includes('{{') &&
          !button.example?.trim()
        ) {
          errors[`button_${index}_example`] = t(
            `${errorKeyPrefix}.ERROR_EXAMPLE_REQUIRED`
          );
        }
        break;

      case BUTTON_TYPES.PHONE_NUMBER: {
        const phoneValue = button[phoneField];
        if (!phoneValue?.trim()) {
          errors[`button_${index}_phone`] = t(
            `${errorKeyPrefix}.ERROR_PHONE_REQUIRED`
          );
        } else if (!validatePhoneE164(phoneValue)) {
          errors[`button_${index}_phone`] = t(
            `${errorKeyPrefix}.PHONE_INVALID`
          );
        }
        break;
      }

      case BUTTON_TYPES.COPY_CODE: {
        const codeValue = button[codeField];
        if (!codeValue?.trim()) {
          errors[`button_${index}_code`] = t(
            `${errorKeyPrefix}.ERROR_CODE_REQUIRED`
          );
        } else if (codeValue.length > getCodeMaxLength()) {
          errors[`button_${index}_code`] = t(
            `${errorKeyPrefix}.CODE_TOO_LONG`,
            {
              max: getCodeMaxLength(),
            }
          );
        } else if (!validateCopyCode(codeValue)) {
          errors[`button_${index}_code`] = t(
            `${errorKeyPrefix}.ERROR_CODE_INVALID_FORMAT`
          );
        }
        break;
      }

      case BUTTON_TYPES.VOICE_CALL:
        if (
          !button.ttl_minutes ||
          !validateTTL(button.ttl_minutes, getMinTTL(), getMaxTTL())
        ) {
          errors[`button_${index}_ttl`] = t(
            `${errorKeyPrefix}.ERROR_TTL_REQUIRED`,
            {
              min: getMinTTL(),
              max: getMaxTTL(),
            }
          );
        }
        break;

      case BUTTON_TYPES.VOICE_CALL_REQUEST:
        // Only title validation, which is already done above
        break;

      default:
        break;
    }

    return errors;
  };

  /**
   * Validate all buttons and return combined errors
   */
  const validateAllButtons = () => {
    const allErrors = {};

    buttons.value.forEach((button, index) => {
      const buttonErrors = validateButton(button, index);
      Object.assign(allErrors, buttonErrors);
    });

    return allErrors;
  };

  /**
   * Check if all buttons are valid
   */
  const isValid = computed(() => {
    if (buttons.value.length === 0) return false;

    return buttons.value.every((btn, index) => {
      const errors = validateButton(btn, index);
      return Object.keys(errors).length === 0;
    });
  });

  return {
    validateButton,
    validateAllButtons,
    isValid,
    // Expose individual validators for use outside
    validatePhoneE164,
    validateUrl,
    validateCopyCode,
    validateNoVariablesInText,
    validateUrlVariablesAtEnd,
    validateTTL,
  };
}
